import React, { useState, useRef } from "react";
import jsPDF from "jspdf";
import "../parkingForms/form.css";

function MonthlyRental() {
  const [formData, setFormData] = useState({
    customerName: "",
    contactNumber: "",
    email: "",
    vehicleNumber: "",
    vehicleType: "",
    bookedDate: "",
    parkingSlot: "",
    rentalAmount: "",
    vehicleImage: null,
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isCameraOn, setIsCameraOn] = useState(false);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "vehicleType") {
      let apiUrl = "";

      if (value === "Bike") {
        apiUrl = `${process.env.REACT_APP_BASE_API_URL}/twoWheelerSlots/api/get`;
      } else if (value === "lmv") {
        apiUrl = `${process.env.REACT_APP_BASE_API_URL}/lmvSlots/api/get`;
      }
      else if (value === "hmv") {
        apiUrl = `${process.env.REACT_APP_BASE_API_URL}/hmvSlots/api/get`;
      }

      if (apiUrl) {
        try {
          const response = await fetch(apiUrl);
          if (!response.ok) throw new Error("Failed to fetch slots");
          const data = await response.json();

          if (Array.isArray(data.data)) {
            setAvailableSlots(data.data);
          } else {
            setAvailableSlots([]);
          }
        } catch (err) {
          console.error("Error fetching slots:", err);
          setAvailableSlots([]);
        }
      } else {
        // Clear slots for unsupported types
        setAvailableSlots([]);
        setFormData((prev) => ({ ...prev, parkingSlot: "" }));
      }
    }
  };

  const startCamera = async () => {
    setIsCameraOn(true);
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    videoRef.current.srcObject = stream;
  };

  const captureImage = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      setCapturedImage(URL.createObjectURL(blob));
      setFormData((prev) => ({ ...prev, vehicleImage: blob }));
    }, "image/png");

    video.srcObject.getTracks().forEach((track) => track.stop());
    setIsCameraOn(false);
  };

  // const blobToBase64 = (blob) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onloadend = () => resolve(reader.result);
  //     reader.onerror = reject;
  //     reader.readAsDataURL(blob);
  //   });

  const generatePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add logo to top-left (replace with your actual base64 logo)
    const logoImg = "data:image/png;base64,..."; // <-- Replace with your actual base64 logo
    //doc.addImage(logoImg, "PNG", 10, 10, 20, 20); // x=10, y=10, width=20, height=20

    // Center the heading
    const title = "Monthly Rental Parking Details";
    doc.setFont("times", "bold");
    doc.setFontSize(16);
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, 20);

    doc.setFontSize(12);
    doc.setFont("times", "normal");

    const lineHeight = 8;
    const yStart = 40;

    // Left side - Customer Info
    const customerDetails = [
      { label: "Customer Name", value: formData.customerName },
      { label: "Contact Number", value: formData.contactNumber },
      { label: "Email", value: formData.email || "N/A" },
    ];

    // Right side - Vehicle Info
    const vehicleDetails = [
      { label: "Vehicle Number", value: formData.vehicleNumber },
      { label: "Vehicle Type", value: formData.vehicleType },
      { label: "Booked Date", value: formData.bookedDate },
      { label: "Parking Slot", value: formData.parkingSlot },
      { label: "Rental Amount", value: `₹${formData.rentalAmount}` },
    ];

    // Draw left side (Customer Details)
    let y = yStart;
    customerDetails.forEach((field) => {
      doc.text(`${field.label}: ${field.value}`, 20, y);
      y += lineHeight;
    });

    // Draw right side (Vehicle Details)
    y = yStart;
    vehicleDetails.forEach((field) => {
      doc.text(`${field.label}: ${field.value}`, pageWidth / 2 + 10, y);
      y += lineHeight;
    });

    // Add vehicle image if available
    if (formData.vehicleImage) {
      const base64Img = await blobToBase64(formData.vehicleImage);
      doc.text("Vehicle Image:", 20, y + 10);
      doc.addImage(base64Img, "PNG", 20, y + 15, 50, 30);
      y += 50;
    }

    // Output the PDF as a blob
    const pdfBlob = doc.output("blob");
    const fileName = `${formData.customerName.replace(/\s+/g, "-").toLowerCase()}_${new Date().toISOString().split("T")[0]}.pdf`;

    const pdfFormData = new FormData();
    pdfFormData.append("pdf", pdfBlob, fileName);
    pdfFormData.append("customerName", formData.customerName);
    pdfFormData.append("bookedDate", formData.bookedDate);

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/certificate/api/save-pdf`, {
        method: "POST",
        body: pdfFormData,
      });

      const result = await res.text();
      if (res.ok) {
        alert("PDF saved to backend!");
      } else {
        alert("Failed to save PDF: " + result);
      }
    } catch (err) {
      console.error(err);
      alert("PDF upload error");
    }
  };

  // Helper function to convert Blob to Base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    formDataToSend.append("customerName", formData.customerName);
    formDataToSend.append("contactNumber", formData.contactNumber);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("vehicleNumber", formData.vehicleNumber);
    formDataToSend.append("vehicleType", formData.vehicleType);
    formDataToSend.append("bookedDate", formData.bookedDate);
    formDataToSend.append("parkingSlot", formData.parkingSlot);
    formDataToSend.append("rentalAmount", formData.rentalAmount);

    if (formData.vehicleImage) {
      formDataToSend.append("vehicleImage", formData.vehicleImage, "vehicle-image.png");
    }

    const parkingResponse = await fetch(`${process.env.REACT_APP_BASE_API_URL}/monthlyRentalParking/api/create`, {
      method: "POST",
      body: formDataToSend,
    });

    if (!parkingResponse.ok) throw new Error("Monthly rental parking registration failed!");

    alert("Data successfully stored in both databases!");

    await generatePDF();
    handleClear();
  };

  const handleClear = () => {
    setFormData({
      customerName: "",
      contactNumber: "",
      email: "",
      vehicleNumber: "",
      vehicleType: "",
      bookedDate: "",
      parkingSlot: "",
      rentalAmount: "",
      vehicleImage: null,
    });
    setCapturedImage(null);
  };

  return (
    <div className="monthlyRental container">
      <form className="p-4 border rounded shadow" onSubmit={handleSubmit}>
        <h2>Enter Monthly Vehicle Rental Details</h2>
        <p>Enter all necessary details correctly...</p>

        <div className="form-group">
          <label>Customer Name</label>
          <input
            type="text"
            name="customerName"
            className="form-control"
            placeholder="Enter Name"
            value={formData.customerName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Contact Number</label>
          <input
            type="tel"
            name="contactNumber"
            className="form-control"
            placeholder="Enter Contact Number"
            value={formData.contactNumber}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>E-mail Id</label>
          <input
            type="email"
            name="email"
            className="form-control"
            placeholder="Enter E-mail (Optional)"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Vehicle Number</label>
          <input
            type="text"
            name="vehicleNumber"
            className="form-control"
            placeholder="Enter Vehicle Number"
            value={formData.vehicleNumber}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Vehicle Type</label>
          <select
            name="vehicleType"
            className="form-control"
            value={formData.vehicleType}
            onChange={handleChange}
            required
          >
            <option value="" disabled>Select Vehicle Type</option>
            <option value="Bike">Two Wheeler</option>
            <option value="lmv">Lmv/Car</option>
            <option value="hmv">Hmv/Truck</option>
          </select>
        </div>

        {(formData.vehicleType === "Bike" || formData.vehicleType === "lmv" || formData.vehicleType === "hmv") && (
          <div className="form-group">
            <label>Parking Slot ({formData.vehicleType})</label>
            <select
              name="parkingSlot"
              className="form-control"
              value={formData.parkingSlot}
              onChange={handleChange}
              required
            >
              <option value="" disabled>Select Available Slots</option>
              {availableSlots.map((slot) => (
                <option key={slot._id} value={slot.slots}>
                  {slot.slots}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Slot Booked Date</label>
          <input
            type="date"
            name="bookedDate"
            className="form-control"
            value={formData.bookedDate}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Monthly Rental Amount</label>
          <input
            type="number"
            name="rentalAmount"
            placeholder="Enter Monthly Rental Amount"
            className="form-control"
            value={formData.rentalAmount}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Capture Vehicle Image</label>
          {!isCameraOn ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={startCamera}
            >
              Open Camera
            </button>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                className="border rounded w-100"
              ></video>
              <button
                type="button"
                className="btn btn-success mt-2"
                onClick={captureImage}
              >
                Capture Image
              </button>
            </>
          )}

          {capturedImage && (
            <div className="mt-3">
              <p>Captured Image Preview:</p>
              <img
                src={capturedImage}
                alt="Captured"
                className="img-thumbnail"
                width="200"
              />
            </div>
          )}
        </div>

        <div className="action text-center mt-3">
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
          <button
            type="button"
            className="btn btn-danger ml-2"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>
      </form>

      <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
    </div>
  );
}

export default MonthlyRental;
